// Form Validation
interface ValidationOptions {
  required?: boolean;
  minChar?: number;
  maxChar?: number;
  customErrorMsg?: {
    required?: string;
    minChar?: string;
    maxChar?: string;
    validInput?: string;
  };
}

export const validateRequired = (
  input: string | number,
  customErrorMsg?: string,
): string => {
  const errorMessage = customErrorMsg ?? "This field is required";
  if (input === null || input === undefined) return errorMessage;
  if (typeof input === "number" && input <= 0) return errorMessage;
  if (typeof input === "string" && input.trim().length === 0)
    return errorMessage;
  return "";
};

const validateCharacterLength = (
  input: string,
  minChar: number | undefined,
  maxChar: number | undefined,
  customErrorMsg: string | undefined,
): string => {
  let errorMsg = "";

  if (minChar !== undefined && input.trim().length < minChar) {
    errorMsg =
      customErrorMsg ??
      `This field must be at least ${minChar} characters long.`;
  } else if (maxChar !== undefined && input.trim().length > maxChar) {
    errorMsg =
      customErrorMsg ??
      `This field must be at most ${maxChar} characters long.`;
  }

  return errorMsg;
};

export const validateBase = (
  input: string,
  options: ValidationOptions = {},
): string => {
  const { minChar, maxChar, customErrorMsg = {} } = options;

  let errorMsg = validateRequired(input, customErrorMsg.required);
  if (errorMsg) return errorMsg;

  errorMsg = validateCharacterLength(
    input,
    minChar,
    maxChar,
    customErrorMsg.minChar || customErrorMsg.maxChar,
  );
  if (errorMsg) return errorMsg;
  return "";
};

export const validateMobile = (
  input: string,
  options: ValidationOptions = {},
): string => {
  const { minChar, maxChar, customErrorMsg = {} } = options;

  let errorMsg = validateRequired(
    input,
    customErrorMsg.required ?? "Mobile number is required.",
  );
  if (errorMsg) return errorMsg;

  errorMsg = validateCharacterLength(
    input,
    minChar,
    maxChar,
    (customErrorMsg.minChar ??
      `Mobile number must be at least ${minChar} digits long.`) ||
      (customErrorMsg.maxChar ??
        `Mobile number must be at most ${maxChar} digits long.`),
  );
  if (errorMsg) return errorMsg;

  if (input.length !== 10) {
    return (
      customErrorMsg.validInput ||
      "Please enter a valid mobile number e.g. 0400123456."
    );
  }

  const testInput = /^(04\d{8})$/.test(input);
  if (!testInput) {
    return (
      customErrorMsg.validInput ||
      "Please enter a valid mobile number e.g. 0400123456."
    );
  }

  return "";
};

export const validatePhone = (
  input: string,
  options: ValidationOptions = {},
): string => {
  const { minChar, maxChar, customErrorMsg = {} } = options;

  let errorMsg = validateRequired(
    input,
    customErrorMsg.required ?? "Phone number is required.",
  );
  if (errorMsg) return errorMsg;

  errorMsg = validateCharacterLength(
    input,
    minChar,
    maxChar,
    (customErrorMsg.minChar ??
      `Phone number must be at least ${minChar} digits long.`) ||
      (customErrorMsg.maxChar ??
        `Phone number must be at most ${maxChar} digits long.`),
  );
  if (errorMsg) return errorMsg;

  if (input.length !== 10)
    return (
      customErrorMsg.validInput ||
      "Please enter a valid 10 digit Australian phone number"
    );

  const testInput = /^(0[23478]\d{8})$/.test(input);
  if (!testInput) {
    return (
      customErrorMsg.validInput ||
      "Please enter a valid 10 digit Australian phone number"
    );
  }

  return "";
};

export const validateEmail = (
  input: string,
  options: ValidationOptions = {},
): string => {
  const { minChar, maxChar, customErrorMsg = {} } = options;

  let errorMsg = validateRequired(
    input,
    customErrorMsg.required ?? "Email address is required.",
  );
  if (errorMsg) return errorMsg;

  errorMsg = validateCharacterLength(
    input,
    minChar,
    maxChar,
    (customErrorMsg.minChar ??
      `Email address must be at least ${minChar} characters long.`) ||
      (customErrorMsg.maxChar ??
        `Email address must be at most ${maxChar} characters long.`),
  );
  if (errorMsg) return errorMsg;

  if (input.length > 254) {
    return customErrorMsg.validInput || "Please enter a valid email address.";
  }

  const testInput = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/.test(
    input,
  );

  if (!testInput) {
    return customErrorMsg.validInput || "Please enter a valid email address.";
  }

  return "";
};

export const validateDateOver18 = (
  input: string,
  customErrorMsg?: string,
): string => {
  const errorMsg = validateRequired(input, "Date of birth is required.");
  if (errorMsg) return errorMsg;

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0"); // January is 0!
  const yyyy = today.getFullYear() - 18;
  const maxDate = `${yyyy}-${mm}-${dd}`;

  if (maxDate < input) {
    if (customErrorMsg) {
      return customErrorMsg;
    } else {
      return `Date of birth must be older than ${dd}/${mm}/${yyyy}.`;
    }
  }

  return "";
};

export const labelCleanUp = (label: string) => {
  return label
    .replace(/[^a-z0-9 ]/gi, "")
    .replace(/ /g, "_")
    .toLowerCase();
};
